<script setup>
import { defineProps, defineEmits, ref, onMounted, onBeforeUnmount } from 'vue'
import MySQLManagerView from '../views/server/MySQLManagerView.vue'
import ComparableDataManagerView from '../views/server/ComparableDataManagerView.vue'
import BackupsView from '../views/server/BackupsView.vue'
import RestoreView from '../views/server/RestoreView.vue'
import KubernetesView from '../views/server/KubernetesView.vue'
import DockerView from '../views/server/DockerView.vue'
import MetricsView from '../views/server/MetricsView.vue'
import LogsView from '../views/server/LogsView.vue'
import DatabaseConnectionView from '../views/server/DatabaseConnectionView.vue'
import UserManagementView from '../views/server/UserManagementView.vue'
import PortsView from '../views/server/PortsView.vue'
import DatabaseTableManagerView from '../views/server/DatabaseTableManagerView.vue'
import SFTPManagerView from '../views/server/SFTPManagerView.vue'
import ServerFileManagerView from '../views/server/ServerFileManagerView.vue'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])
const currentView = ref('mysql')
const sidebarCollapsed = ref(false)

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

const closeOverlay = () => {
  emit('close')
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && props.show) {
    closeOverlay()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
})

const navigateTo = (view) => {
  currentView.value = view
}

// Component mapping
const viewComponents = {
  mysql: MySQLManagerView,
  'comparable-data': ComparableDataManagerView,
  backups: BackupsView,
  restore: RestoreView,
  kubernetes: KubernetesView,
  docker: DockerView,
  metrics: MetricsView,
  logs: LogsView,
  connections: DatabaseConnectionView,
  users: UserManagementView,
  ports: PortsView,
  'table-manager': DatabaseTableManagerView,
  'sftp-manager': SFTPManagerView,
  'server-files': ServerFileManagerView
}
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-8">
    <div class="bg-white rounded-lg shadow-xl w-full h-full flex overflow-hidden"
         @click.stop>
      <!-- Left Side Navigation -->
      <div class="bg-gray-100 border-r border-gray-200 transition-all duration-300"
           :class="sidebarCollapsed ? 'w-16' : 'w-64'">
        <div class="p-4">
        <div class="flex items-center justify-between mb-6">
            <h2 v-if="!sidebarCollapsed" class="text-xl font-semibold text-gray-800">Server Manager</h2>
            <div class="flex items-center space-x-2">
              <button @click="toggleSidebar" class="text-gray-500 hover:text-gray-700" :title="sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'">
                <i :class="sidebarCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
              </button>
          <button @click="closeOverlay" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
            </div>
          </div>
        </div>
        
        <nav class="space-y-2 px-4">
          <!-- File Management Section -->
          <div class="space-y-1">
            <div v-if="!sidebarCollapsed" class="font-medium text-gray-700 px-3 py-2">
              <i class="fas fa-folder mr-2"></i>
              File Management
            </div>
            <div v-else class="border-b border-gray-300 mb-2 pb-2">
              <div class="px-3 py-2 text-center" title="File Management">
                <i class="fas fa-folder text-gray-600"></i>
              </div>
            </div>
            <button 
              @click="navigateTo('server-files')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'server-files',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Server File Manager' : ''"
            >
              <i class="fas fa-folder-open" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Server File Manager</span>
            </button>
            <button 
              @click="navigateTo('sftp-manager')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'sftp-manager',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'S/FTP Manager' : ''"
            >
              <i class="fas fa-server" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">S/FTP Manager</span>
            </button>
          </div>

          <!-- Database Manager Section -->
          <div class="space-y-1">
            <div v-if="!sidebarCollapsed" class="font-medium text-gray-700 px-3 py-2">
              <i class="fas fa-database mr-2"></i>
              Database Manager
            </div>
            <div v-else class="border-b border-gray-300 mb-2 pb-2">
              <div class="px-3 py-2 text-center" title="Database Manager">
                <i class="fas fa-database text-gray-600"></i>
              </div>
            </div>
            <button 
              @click="navigateTo('connections')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'connections',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Database Connections' : ''"
            >
              <i class="fas fa-plug" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Database Connections</span>
            </button>
            <button 
              @click="navigateTo('table-manager')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'table-manager',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Table Manager' : ''"
            >
              <i class="fas fa-table" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Table Manager</span>
            </button>
            <button 
              @click="navigateTo('mysql')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'mysql',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'MySQL Manager' : ''"
            >
              <i class="fas fa-database" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">MySQL Manager</span>
            </button>
            <button 
              @click="navigateTo('comparable-data')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'comparable-data',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Comparable Data MySQL' : ''"
            >
              <i class="fas fa-chart-line" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Comparable Data MySQL</span>
            </button>
            <button 
              @click="navigateTo('backups')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'backups',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Backups' : ''"
            >
              <i class="fas fa-save" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Backups</span>
            </button>
            <button 
              @click="navigateTo('restore')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'restore',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Restore' : ''"
            >
              <i class="fas fa-undo" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Restore</span>
            </button>
          </div>

          <!-- Container Management Section -->
          <div class="space-y-1">
            <div v-if="!sidebarCollapsed" class="font-medium text-gray-700 px-3 py-2">
              <i class="fas fa-cube mr-2"></i>
              Container Management
            </div>
            <div v-else class="border-b border-gray-300 mb-2 pb-2">
              <div class="px-3 py-2 text-center" title="Container Management">
                <i class="fas fa-cube text-gray-600"></i>
              </div>
            </div>
            <button 
              @click="navigateTo('kubernetes')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'kubernetes',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Kubernetes' : ''"
            >
              <i class="fas fa-dharmachakra" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Kubernetes</span>
            </button>
            <button 
              @click="navigateTo('docker')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'docker',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Docker' : ''"
            >
              <i class="fab fa-docker" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Docker</span>
            </button>
          </div>

          <!-- Monitoring Section -->
          <div class="space-y-1">
            <div v-if="!sidebarCollapsed" class="font-medium text-gray-700 px-3 py-2">
              <i class="fas fa-chart-line mr-2"></i>
              Monitoring
            </div>
            <div v-else class="border-b border-gray-300 mb-2 pb-2">
              <div class="px-3 py-2 text-center" title="Monitoring">
                <i class="fas fa-chart-line text-gray-600"></i>
              </div>
            </div>
            <button 
              @click="navigateTo('metrics')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'metrics',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Metrics' : ''"
            >
              <i class="fas fa-chart-bar" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Metrics</span>
            </button>
            <button 
              @click="navigateTo('logs')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'logs',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Logs' : ''"
            >
              <i class="fas fa-file-alt" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Logs</span>
            </button>
          </div>

          <!-- User Management Section -->
          <div class="space-y-1">
            <div v-if="!sidebarCollapsed" class="font-medium text-gray-700 px-3 py-2">
              <i class="fas fa-users mr-2"></i>
              User Management
            </div>
            <div v-else class="border-b border-gray-300 mb-2 pb-2">
              <div class="px-3 py-2 text-center" title="User Management">
                <i class="fas fa-users text-gray-600"></i>
              </div>
            </div>
            <button 
              @click="navigateTo('users')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'users',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Users & Activity' : ''"
            >
              <i class="fas fa-user-shield" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Users & Activity</span>
            </button>
            <button 
              @click="navigateTo('ports')"
              class="block w-full text-gray-600 hover:bg-gray-200 rounded-lg"
              :class="{
                'bg-pink-50 text-pink-500': currentView === 'ports',
                'text-left px-3 py-2': !sidebarCollapsed,
                'text-center px-2 py-2': sidebarCollapsed
              }"
              :title="sidebarCollapsed ? 'Network Ports' : ''"
            >
              <i class="fas fa-network-wired" :class="sidebarCollapsed ? '' : 'mr-2'"></i>
              <span v-if="!sidebarCollapsed">Network Ports</span>
            </button>
          </div>
        </nav>
      </div>

      <!-- Right Side Content -->
      <div class="flex-1 p-6 overflow-y-auto">
        <component :is="viewComponents[currentView]" />
      </div>
    </div>
  </div>
</template> 